import MarkerClusterer from '@google/markerclustererplus';
import {
  Coordinate,
  MarkerAction,
  RetailerMarker,
  RetailerEntity,
  RetailerType,
} from '../store/types';
import { getImagePath } from '../../../utils/path';

const rad = (x: number) => {
  return (x * Math.PI) / 180;
};

export const getCoordinatesDistance = (p1: Coordinate, p2: Coordinate) => {
  const R = 6378137; // Earth’s mean radius in meter
  const dLat = rad(p2.lat - p1.lat);
  const dLong = rad(p2.lng - p1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(p1.lat)) * Math.cos(rad(p2.lat)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return Math.round(d / 1000); // returns distance in Km
};

export const centerMap = (map, location, zoom = 10) => {
  map.setCenter(location);
  map.setZoom(zoom);
};

export const displayCurrentLocation = (map: google.maps.Map) => {
  if (!navigator.geolocation) return;

  navigator.geolocation.getCurrentPosition(
    (position: any) => {
      const pos = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      const marker = new google.maps.Marker({
        position: pos,
        map: map,
        icon: {
          url: getImagePath('current-location.png'),
        },
      });
      marker.setPosition(pos);
    },
    () => {
      // Ignore errors for now
    }
  );
};

export const waitForGoogle = (cb: () => void) => {
  const doWait = () => {
    if (typeof google !== 'undefined' && google.maps) {
      cb();
    } else {
      setTimeout(doWait, 100);
    }
  };

  doWait();
};

export const loadMarkers = (map: google.maps.Map, props) => {
  if (!window?.google || !map) {
    return;
  }
  const { retailers, activeRetailer, onRetailerHover } = props;
  const bounds = new google.maps.LatLngBounds();
  if (retailers.length) {
    const markers: google.maps.Marker[] = [];

    retailers.forEach((store: RetailerEntity) => {
      const { id, lat, lng, name, type } = store;
      if (!lat || !lng) {
        return;
      }
      const position = { lat: lat, lng: lng };
      bounds.extend(position);

      let icon: string;

      if (type === RetailerType.BrandSpace) {
        icon = 'brandspace-marker.png';
      } else {
        icon = 'standard-marker.png';
      }

      const marker = new google.maps.Marker({
        ...store,
        position: position,
        map: map,
        title: name,
        icon: {
          url: getImagePath(activeRetailer.id === id ? 'selected-marker.png' : icon),
          // anchor: new google.maps.Point(32, 32),
          scaledSize: new google.maps.Size(32, 40),
        },
      });

      // Activate/Deactivate the marker when hovering in/out
      marker.addListener('click', () => {
        onRetailerHover(store.id, true);
      });
      markers.push(marker);
    });

    //  Group markers together
    new MarkerClusterer(map, markers, {
      imagePath: getImagePath('cluster-marker'),
      ignoreHidden: true,
      maxZoom: 9,
    });

    //Save markers to the store
    props.setMarkers(markers);
  }
};

export const changeMarkerIcon = (id: number, markerAction: MarkerAction, markers) => {
  //Find the marker to be updated
  const marker = markers && markers.filter((marker: RetailerMarker) => marker.id === id)[0];
  // Ensures it's a google map marker, as it crashes sometimes
  if (marker && marker.setIcon) {
    //Default format for inactive icons
    let icon =
      marker.type === RetailerType.BrandSpace ? 'brandspace-marker.png' : 'standard-marker.png';

    // let size = 32;
    let animation = null;
    //Change marker format if active
    if (markerAction === MarkerAction.Activate) {
      icon = 'selected-marker.png';
      // size = 64;
      animation = google.maps.Animation.BOUNCE;
    }

    //Apply format changes
    marker.setIcon({
      url: getImagePath(icon),
      // scaledSize: new google.maps.Size(size, size),
    });
    marker.setAnimation(animation);
  }
};

export const loadAutocomplete = (map, changeMapBounds) => {
  // React reference throws an error when using it in an ant design component, hence using document.getElementById
  // const input = autocompleteRef.current as HTMLInputElement;
  const input = document.getElementById('pac-input') as HTMLInputElement;
  const autocomplete = new google.maps.places.Autocomplete(input);
  autocomplete.bindTo('bounds', map);
  autocomplete.setFields(['address_components', 'geometry', 'icon', 'name']);

  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    if (!place.geometry) {
      // User entered the name of a Place that was not suggested and
      // pressed the Enter key, or the Place Details request failed.
      window.alert("No details available for input: '" + place.name + "'");
      return;
    }

    // If the place has a geometry, then present it on a map.
    if (place.geometry.viewport) {
      map.fitBounds(place.geometry.viewport);
    } else {
      map.setCenter(place.geometry.location);
      map.setZoom(17); // Why 17? Because it looks good.
    }

    // Dispatch changeMapBounds redux action
    changeMapBounds();
  });
};
